/**
 * Vendor-name normalization — the gate for vendor identity (EDG-1087).
 *
 * The same firm prints a dozen ways across bid tabs: "E&B Paving", "E B Paving",
 * "E and B Paving", "E. & B. Paving, LLC". They must all collapse to ONE key so
 * a bidder resolves to the vendor the customer already has from DOT data instead
 * of forking into duplicates. This is deliberately aggressive on the things that
 * vary cosmetically (case, punctuation, the `&`/`and` connector, company
 * suffixes) and nothing else — it is the exact-match key AND (when non-empty)
 * the value stored as `global_vendors.global_normalized_name`, so both sides of
 * a match normalize identically. A name that collapses to empty (e.g. a bare
 * suffix) stores `null` there rather than a non-normalized fallback.
 */

// Trailing company-form suffixes that carry no identity. Word-bounded so a real
// token is never clipped ("Incorporated" → '', but "Lincoln" is left intact).
const SUFFIXES =
  /\b(llc|l\.l\.c|inc|incorporated|co|company|corp|corporation|ltd|limited|llp|lp|pc|pllc|dba)\b/g;

/**
 * Collapse a vendor name to its identity key. "E&B Paving, LLC", "E and B
 * Paving", and "E B PAVING INC" all return `"e b paving"`. Empty/whitespace in →
 * empty out.
 */
export function normalizeVendorName(name: string): string {
  return (
    name
      .toLowerCase()
      // The connector is noise: "&" and a standalone "and" both vanish so the
      // three spellings of "E&B" converge. `\band\b` leaves "Sand"/"Anderson"
      // untouched.
      .replace(/&/g, ' ')
      .replace(/\band\b/g, ' ')
      // Strip suffixes BEFORE flattening punctuation, so the dotted forms
      // ("L.L.C.", "Inc.", "Co.") still match the suffix pattern.
      .replace(SUFFIXES, ' ')
      // Punctuation that only ever separates ("Rieth-Riley", "A.B.C.", "O'Hara",
      // "Smith/Jones") becomes a space so the tokens match their unpunctuated
      // twins.
      .replace(/[.,'/\\-]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
  );
}

// Generic industry nouns that describe WHAT a contractor does, not WHO it is —
// derived from the DOT vendor corpus (each appears on >1% of names). They carry
// almost no identity, so when two names share only these the trigram score is
// inflated ("MS Contracting" vs "MD Contracting" → 0.76 despite ms≠md). Stripped
// ONLY for fuzzy SCORING (see `vendorMatchCore`), never from the stored identity
// key — collapsing them there would wrongly merge "Sapp Paving"/"Sapp Concrete".
// Single source of truth for the noun list — the JS regex and the Postgres
// pattern below are generated from it so they can never drift.
const INDUSTRY_NOUN_LIST = [
  'construction',
  'constructors',
  'contracting',
  'contractors',
  'contractor',
  'paving',
  'excavating',
  'excavation',
  'grading',
  'dozing',
  'asphalt',
  'concrete',
  'materials',
  'aggregate',
  'aggregates',
  'utilities',
  'utility',
  'engineering',
  'engineers',
  'builders',
  'building',
  'services',
  'service',
  'industries',
  'enterprises',
  'solutions',
  'civil',
] as const;

const INDUSTRY_NOUNS = new RegExp(
  `\\b(${INDUSTRY_NOUN_LIST.join('|')})\\b`,
  'g',
);

/**
 * The same industry-noun list as a Postgres `regexp_replace` pattern, using PG
 * word boundaries (`\m…\M`). Exported so the fuzzy-match SQL strips the stored
 * `global_normalized_name` identically to `vendorMatchCore` on the JS side —
 * both must derive from {@link INDUSTRY_NOUN_LIST} so they can never drift.
 */
export const INDUSTRY_NOUNS_SQL = `\\m(${INDUSTRY_NOUN_LIST.join('|')})\\M`;

/**
 * The DISTINCTIVE core used ONLY to score fuzzy similarity — NOT the stored
 * identity key. Takes the normalized name and additionally removes generic
 * industry nouns so the score reflects the distinguishing tokens ("ms" vs "md"),
 * not shared boilerplate ("contracting"). If stripping the nouns leaves NOTHING
 * — a firm whose whole name IS an industry noun, ~12 of 6038 in the corpus — it
 * FALLS BACK to the full normalized name so recall isn't lost. A short-but-
 * present core ("ms", "e b") is kept (that's the distinctive part); the matcher
 * holds such short-core hits for review rather than auto-adopting.
 * The result is compared symmetrically on both sides, so there is no drift.
 */
export function vendorMatchCore(name: string): string {
  const normalized = normalizeVendorName(name);
  const core = normalized
    .replace(INDUSTRY_NOUNS, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  // Fall back ONLY when stripping leaves NOTHING (a firm whose whole name is an
  // industry noun, ~12 of 6038). A short-but-present core ("ms", "e b") is the
  // DISTINCTIVE part and must be kept — that is precisely what discriminates
  // "MS Contracting" from "MD Contracting". Short cores are handled by the
  // matcher (a short-core hit holds for review instead of auto-adopting).
  return core.length > 0 ? core : normalized;
}
